import { Timestamp } from "firebase/firestore";
import { ClientJobV2 } from "../@types/client";

const isTimestampLike = (value: any): value is Timestamp =>
  value !== null &&
  typeof value === "object" &&
  typeof value.toMillis === "function";

// updatedAt is stamped fresh on every save, and line item completedAt is
// re-stamped on every save of a job in a completed stage, so comparing their
// values would make every entry look changed. updatedAt keys are ignored
// outright; completedAt compares null-vs-set so genuine completion
// transitions still register as changes.
const valuesEqual = (a: any, b: any, key?: string): boolean => {
  if (key === "updatedAt") return true;
  if (key === "completedAt") return (a == null) === (b == null);
  if (a == null || b == null) return a == null && b == null;
  if (isTimestampLike(a) || isTimestampLike(b)) {
    return (
      isTimestampLike(a) && isTimestampLike(b) && a.toMillis() === b.toMillis()
    );
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
      return false;
    }
    return a.every((item, index) => valuesEqual(item, b[index]));
  }
  if (typeof a === "object" || typeof b === "object") {
    if (typeof a !== "object" || typeof b !== "object") return false;
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const k of keys) {
      if (!valuesEqual(a[k], b[k], k)) return false;
    }
    return true;
  }
  return a === b;
};

/**
 * True when the client doc's stored entry for a job matches the freshly built
 * one, meaning the write can be skipped. Any doubt (missing entry, comparison
 * failure) reports "changed" so the write still happens.
 */
export function clientJobEntryUnchanged(
  existing: ClientJobV2 | undefined,
  updated: ClientJobV2
): boolean {
  if (!existing) return false;
  try {
    return valuesEqual(existing, updated);
  } catch {
    return false;
  }
}
